import React, { useCallback, useEffect, useState } from 'react';
import {
  AppBar,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import {
  PACKAGES,
  globalPageTypeEntity,
} from '../../constants/applicationConstants';
import DisplayBreadcrumbs from '../../components/displayBreadcrumbs/displayBreadcrumbs';
import OrgsUsers from '../../components/orgsUsers/orgsUsers';
import OrgsProducts from '../../components/orgsProduct/orgsProduct';
import ScriptList from '../scripts/ScriptList';
import ExternalAuthList from '../externalAuth/ExternalAuthList';
import PAConfig from '../PAConfig/PAConfig';
import DisplayDate from '../../components/customListing/displayDate/displayDate';
import { useHistory } from 'react-router-dom';
import { PopupModal, useFeatureFlag } from '../../submodule';
import { OrganizationForm } from './organizationForm';
import { OrganizationDetailTabs, RouteParams } from './types';
import {
  a11yProps,
  breadcrumbsNav,
  tabsConfig,
} from './const';
import { feature_flag_interceptor_management } from '../../common/const';

export const OrganizationDetailPage: React.FC<
  RouteComponentProps<RouteParams>
> = ({ match }) => {
  const history = useHistory();
  const location = useLocation();
  const [orgAction, userAction] = useStoreActions((actions: any) => [
    actions.organization,
    actions.userList,
  ]);
  const orgState = useStoreState((state: any) => state.organization);
  const initialTab =
    Number(new URLSearchParams(location.search).get('tab')) ||
    OrganizationDetailTabs.Users;
  const [currentTab, setCurrentTab] =
    useState<OrganizationDetailTabs>(initialTab);

  const handleTabChange = (
    _e: React.ChangeEvent<{}>,
    value: OrganizationDetailTabs,
  ) => {
    setCurrentTab(value);
    history.replace({ search: `?tab=${value}` });
  };
  const [open, setOpen] = useState(false);
  const isInterceptorManagement = useFeatureFlag(
    feature_flag_interceptor_management,
    false,
  );
  
  const visibleTabs = tabsConfig.filter(tab => {
    if (tab.value === OrganizationDetailTabs.Transformations && !isInterceptorManagement) return false;
    if (tab.value === OrganizationDetailTabs.ExternalAuth && !isInterceptorManagement) return false;
    return true;
  }); 
  const selectedTabIndex = visibleTabs.findIndex(
    tab => tab.value === currentTab
  );

  const orgId: string = orgState.orgsData?.PK ?? orgState.addedOrganizationId;

  const getDetails = useCallback(
    async (id: string) => {
      const response = await orgAction.getOrgs(id);
      if (response?.payload?.error) {
        // error handled by the thunk
      }
    },
    [orgAction],
  );

  useEffect(() => {
    const orgsFormData = orgState.orgsData;
    if (orgsFormData) {
      orgsFormData.packages = orgsFormData.packages
        ? [...orgsFormData.packages, PACKAGES.base]
        : [PACKAGES.base];
    }
  }, [orgState.orgsData]);

  useEffect(() => {
    if (match?.params?.id) {
      getDetails(match.params.id);
    } else {
      orgAction.setOrgs({});
      userAction.setUserList([]);
    }
    orgAction.setAddedOrganizationId('');
  }, [getDetails, match.params.id, orgAction, userAction]);

  if (!orgState.orgsData?.shortname) return null;

  const { orgsData } = orgState;

  const address = [
    orgsData.address,
    orgsData.state,
    orgsData.city,
    orgsData.zipcode,
  ]
    .filter((item: any) => item != null && item !== '')
    .join(',');

  const domains =
    orgsData.config?.allowed_domains
      ?.map((item: any) => item.domain)
      ?.join(',') || 'N/A';

  return (
    <Grid container direction="column">
      <DisplayBreadcrumbs items={breadcrumbsNav} />
      <Grid item sm={12}>
        <Grid
          container
          style={{
            padding: '50px 30px',
            margin: '0 -16px',
            wordBreak: 'break-all',
          }}
          spacing={2}
        >
          <Grid item sm={10}>
            <Typography variant="h6" component="h6">
              {orgsData.shortname} - {orgsData.title}
              <Tooltip
                title="Edit Tenant"
                style={{ marginLeft: 20, cursor: 'pointer' }}
              >
                <EditIcon color="primary" onClick={() => setOpen(true)} />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid item sm={2}>
            <Typography
              style={{
                cursor: 'pointer',
                fontSize: 14,
                width: 'fit-content',
                float: 'right',
              }}
              variant="h6"
              component="h6"
              onClick={() => history.push('/organization')}
            >
              <KeyboardBackspaceIcon style={{ position: 'relative', top: 7 }} />{' '}
              Back to List
            </Typography>
          </Grid>
          <Grid item sm={2}>
            <DisplayDate
              label="Effective Date"
              date={orgsData.effectivedate ?? null}
            />
          </Grid>
          <Grid item sm={2}>
            <DisplayDate
              label="End Date"
              date={orgsData.inactivedate ?? null}
            />
          </Grid>
          <Grid item sm={2}>
            Domain: {domains}
          </Grid>
          <Grid item sm={2}>
            Address: {address}
          </Grid>
        </Grid>
      </Grid>
      <Grid item sm={12}>
        <AppBar position="static">
          <Tabs
            className="tabsHeading"
            value={selectedTabIndex}
            // onChange={handleTabChange}
            onChange={(e, newIndex) => {
              const selectedTab = visibleTabs[newIndex];
              handleTabChange(e, selectedTab.value); // pass enum value
            }}
            indicatorColor="secondary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label="simple tabs example"
          >
            {visibleTabs.map((tab, index) => (
              <Tab key={tab.value} label={tab.label} {...a11yProps(index)} />
            ))}
          </Tabs>
        </AppBar>
      </Grid>
      {currentTab === OrganizationDetailTabs.Users && (
        <Grid style={{ padding: 50 }} item sm={12}>
          <OrgsUsers addedOrgsId={orgId} pageType="edit" />
        </Grid>
      )}
      {currentTab === OrganizationDetailTabs.Products && (
        <Grid item sm={12} style={{ padding: 50 }}>
          <OrgsProducts
            organizationData={orgsData}
            products={orgsData.products ?? []}
            availableProduct={orgsData.app_products ?? []}
            reloadOrg={() => getDetails(match.params.id)}
          />
        </Grid>
      )}
      {isInterceptorManagement && (
        <>
          {currentTab === OrganizationDetailTabs.Transformations && (
            <Grid item sm={12} style={{ padding: 50 }}>
              <ScriptList orgId={orgsData.PK} />
            </Grid>
          )}
          {currentTab === OrganizationDetailTabs.ExternalAuth && (
            <Grid item sm={12} style={{ padding: 50 }}>
              <ExternalAuthList orgId={orgsData.PK} />
            </Grid>
          )}
        </>
      )}
      {currentTab === OrganizationDetailTabs.PAConfig && (
        <Grid item sm={12} style={{ padding: 50 }}>
          <PAConfig orgId={orgsData.PK} />
        </Grid>
      )}
      <PopupModal
        open={open}
        isDirtyForm={undefined}
        setModal={undefined}
        maxWidth={undefined}
      >
        <OrganizationForm
          onClose={(type: string) => {
            if (type === globalPageTypeEntity.edit) {
              getDetails(match.params.id);
            }
            setOpen(false);
          }}
          type={globalPageTypeEntity.edit}
          formDate={{
            ...orgsData,
            allowed_domains: orgsData.config?.allowed_domains?.map(
              (item: any) => item.domain,
            ),
          }}
        />
      </PopupModal>
    </Grid>
  );
};
